import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Loader2, FileText, Phone, MessageCircle, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { API } from "@/lib/api";
import { PHONE_DISPLAY, PHONE_TEL, WHATSAPP_NUMBER } from "@/lib/i18n";

export default function PublicQuote() {
  const { ref } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("t") || "";
  const [quote, setQuote] = useState(null);
  const [split, setSplit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/quotes/${ref}`, { params: { token } });
        setQuote(data);
        // Load payment-split (best-effort — not critical if this fails)
        try {
          const s = await axios.get(`${API}/quotes/${ref}/payment-split`, { params: { token } });
          setSplit(s.data);
        } catch { /* ignore */ }
      } catch (err) {
        setError(err?.response?.data?.detail || "Quote not found");
      } finally {
        setLoading(false);
      }
    })();
  }, [ref, token]);

  const waMsg = quote
    ? `Hello Juke Transfers, I'd like to accept quote ${quote.ref_number} (${quote.pickup} → ${quote.dropoff}, €${Math.round(quote.total_price_eur)}).`
    : "";
  const waLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(waMsg)}`;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] text-zinc-100 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#C5A059] animate-spin" />
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col items-center justify-center px-6 text-center">
        <p className="font-display text-3xl text-white font-light mb-3">Quote not found</p>
        <p className="text-zinc-500">{error}</p>
        <a href="/" className="mt-6 text-[#C5A059] hover:text-[#D4AF37]">← Back to Juke Transfers</a>
      </div>
    );
  }

  const dur = quote.duration_min || 0;
  const durStr = `${Math.floor(dur / 60)}h ${dur % 60}m`;

  return (
    <div data-testid="public-quote-page" className="min-h-screen bg-[#050505] text-zinc-100 py-16 px-6">
      <div className="max-w-3xl mx-auto">
        <header className="flex justify-between items-center mb-12">
          <div className="flex items-center gap-2">
            <span className="font-display text-2xl text-white">Juke</span>
            <span className="font-display text-2xl text-[#C5A059] italic">Transfers</span>
          </div>
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">Private Quote</p>
        </header>

        <div className="rounded-2xl border border-white/10 bg-[#0d0d0d] overflow-hidden">
          <div className="px-8 lg:px-12 py-10 border-b border-white/10 flex flex-col sm:flex-row justify-between items-start gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-3">Quote Reference</p>
              <p className="font-mono text-2xl text-white">{quote.ref_number}</p>
              <p className="text-xs text-zinc-600 mt-2">
                Issued {new Date(quote.created_at).toLocaleDateString(undefined, { dateStyle: "medium" })}
              </p>
            </div>
            <div className="sm:text-right">
              <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Prepared for</p>
              <p className="font-display text-xl text-white font-light">{quote.guest_name}</p>
              {quote.guest_email && <p className="text-sm text-zinc-500 mt-1">{quote.guest_email}</p>}
            </div>
          </div>

          <div className="px-8 lg:px-12 py-10 bg-gradient-to-br from-[#C5A059]/[0.04] to-transparent">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-3">Total estimate</p>
            <p className="font-display text-6xl text-white font-light tracking-tight">€{Number(quote.total_price_eur).toFixed(2)}</p>
            <p className="text-sm text-zinc-500 mt-3">
              All-inclusive · No surge fees · Fixed price upon confirmation
              {quote.night_surcharge ? " · Includes night surcharge" : ""}
            </p>
          </div>

          {split && split.mode !== "default" && (
            <div
              data-testid="quote-payment-split"
              className="px-8 lg:px-12 py-8 border-t border-white/10 bg-[#0a0a0a]"
            >
              <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-5">Payment split</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Total price</p>
                  <p className="mt-1 font-display text-2xl text-white font-light tabular-nums">€{Number(split.total).toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Due now · Reservation fee</p>
                  <p className="mt-1 font-display text-2xl text-[#C5A059] font-semibold tabular-nums">€{Number(split.due_now).toFixed(2)}</p>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">Paid online via secure link</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Remaining balance</p>
                  <p className="mt-1 font-display text-2xl text-white font-light tabular-nums">€{Number(split.remaining).toFixed(2)}</p>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                    {split.remaining_payment_location || "Paid directly to the supplier / on board"}
                  </p>
                </div>
              </div>
              {quote.stripe_checkout_url && (
                <a
                  href={quote.stripe_checkout_url}
                  target="_blank" rel="noreferrer"
                  data-testid="quote-pay-now-btn"
                  className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#C5A059] hover:bg-[#D4AF37] text-black text-[11px] uppercase tracking-[0.25em] font-semibold"
                >
                  Pay Reservation Fee · €{Number(split.due_now).toFixed(2)}
                </a>
              )}
            </div>
          )}

          <div className="px-8 lg:px-12 py-10 border-t border-white/10">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-6">Journey</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-6 gap-x-10">
              <Item label="Pickup" value={quote.pickup} />
              <Item label="Drop-off" value={quote.dropoff} />
              <Item label="Date" value={quote.pickup_date || "—"} />
              <Item label="Time" value={quote.pickup_time || "—"} />
              <Item label="Distance" value={`${quote.distance_km} km`} />
              <Item label="Drive time" value={durStr} />
              <Item label="Vehicle" value={quote.vehicle_type} />
              <Item label="Passengers" value={quote.passengers} />
            </div>
            {quote.notes && (
              <div className="mt-8 pt-6 border-t border-white/10">
                <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Notes</p>
                <p className="text-zinc-300 leading-relaxed">{quote.notes}</p>
              </div>
            )}
          </div>

          <div className="px-8 lg:px-12 py-8 border-t border-white/10 bg-[#080808]">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-4">Confirm your booking</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="public-quote-whatsapp"
                className="flex-1 flex items-center justify-center gap-2 h-12 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white font-semibold transition-colors shadow-[0_4px_18px_rgba(37,211,102,0.35)]"
              >
                <MessageCircle className="w-4 h-4" /> Accept on WhatsApp
              </a>
              <a
                href={`tel:${PHONE_TEL}`}
                data-testid="public-quote-phone"
                className="flex-1 flex items-center justify-center gap-2 h-12 rounded-full border border-[#C5A059]/40 text-[#C5A059] hover:bg-[#C5A059]/10 font-semibold transition-colors"
              >
                <Phone className="w-4 h-4" /> {PHONE_DISPLAY}
              </a>
              <a
                href={`${API}/quotes/${quote.ref_number}/pdf?token=${encodeURIComponent(token)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="public-quote-pdf"
                className="flex-1 flex items-center justify-center gap-2 h-12 rounded-full border border-white/15 text-zinc-200 hover:text-[#C5A059] hover:border-[#C5A059] transition-colors"
              >
                <FileText className="w-4 h-4" /> Download PDF
              </a>
            </div>
            <p className="text-xs text-zinc-600 mt-5 flex items-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 text-[#C5A059]" />
              Quote valid for 14 days. Licensed & fully insured private transfer operator.
            </p>
          </div>
        </div>

        <p className="text-center text-xs text-zinc-600 mt-10">
          © {new Date().getFullYear()} Juke Transfers · Split, Croatia · bookings@juketransfers.hr
        </p>
      </div>
    </div>
  );
}

function Item({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</p>
      <p className="text-zinc-100 mt-1.5">{value}</p>
    </div>
  );
}
